import { getCSSVariable, throttleAndDebounce } from '@helper/helpers'

const header = document.querySelector('.main-header')
const logoWrapper = document.querySelector('[name="Header"] .logo-wrapper')

if (!header) {
  throw new Error('Header not found')
}

if (!logoWrapper) {
  throw new Error('Logo wrapper not found')
}

// Configuration
const DEFAULT_LOGO_WIDTH = 250
const SCROLLED_LOGO_WIDTH = 200
const MOBILE_LOGO_WIDTH = 124 // Define mobile logo width
const MOBILE_SCROLLED_LOGO_WIDTH = 100 // Define mobile logo width
const SCROLL_OFFSET = 150 // Offset where the header changes appearance
const downThreshold = 200 // Minimum scroll distance to trigger header visibility change

let lastPos = 0

/**
 * Set the height of the header as a CSS variable
 */
const setHeaderHeight = () => {
  const headerRect = header.getBoundingClientRect()
  const headerHeight = Math.floor(headerRect.height)

  document.documentElement.style.setProperty(
    '--header-height',
    `${headerHeight}px`
  )
}

/**
 * Update the logo width and adjust header height accordingly
 * @param {number} logoWidth - The width to set for the logo
 */
const setLogoWidth = logoWidth => {
  logoWrapper.style.setProperty('--logo-width', `${logoWidth}px`)
  requestAnimationFrame(setHeaderHeight)
}

/**
 * Change the visibility of the header with smooth transition
 * @param {string} headerVisibility - The desired visibility state ('visible' or 'hidden')
 */
const setHeaderVisibility = headerVisibility => {
  const previousVisibility = header.getAttribute('data-header-visibility')
  header.setAttribute('data-header-visibility', headerVisibility)

  if (headerVisibility === 'visible' && previousVisibility === 'hidden') {
    const transitionDuration =
      parseInt(getCSSVariable('--transition-duration', header)) || 300
    setTimeout(() => {
      const currentPos = window.scrollY
      const logoWidth =
        currentPos > SCROLL_OFFSET
          ? getScrolledLogoWidth()
          : getInitialLogoWidth()
      setLogoWidth(logoWidth)
    }, transitionDuration)
  }
}

/**
 * Update the header position based on scroll offset
 * @param {string} headerPosition - The position state of the header ('scrolled' or 'default')
 */
const setHeaderPosition = headerPosition => {
  header.setAttribute('data-header-position', headerPosition)

  // Adjust logo size only if the header is visible
  if (header.getAttribute('data-header-visibility') === 'visible') {
    const logoWidth =
      headerPosition === 'scrolled'
        ? getScrolledLogoWidth()
        : getInitialLogoWidth()
    setLogoWidth(logoWidth)
  }
}
function setHeaderColor(color) {
  header.style.backgroundColor = color
}

/**
 * Determine initial logo width based on screen size
 */
const getInitialLogoWidth = () => {
  return window.matchMedia('(max-width: 700px)').matches
    ? MOBILE_LOGO_WIDTH
    : DEFAULT_LOGO_WIDTH
}

/**
 * Determine scrolled logo width based on screen size
 */
const getScrolledLogoWidth = () => {
  return window.matchMedia('(max-width: 700px)').matches
    ? MOBILE_SCROLLED_LOGO_WIDTH
    : SCROLLED_LOGO_WIDTH
}

/**
 * Handle scroll events to control header visibility and position
 */
const onScroll = () => {
  const currentPos = window.scrollY
  if (header.getAttribute('data-is-transparent') === 'true') {
    if (currentPos > SCROLL_OFFSET * 0.75) {
      setHeaderColor('var(--clr-primary)')
    } else {
      setHeaderColor('transparent')
    }
  }

  // Update header position based on scroll offset
  if (currentPos > SCROLL_OFFSET) {
    setHeaderPosition('scrolled')
  } else {
    setHeaderPosition('default')
  }

  // Adjust header visibility based on scroll direction and threshold
  if (Math.abs(currentPos - lastPos) > downThreshold) {
    if (currentPos > lastPos && currentPos > downThreshold) {
      setHeaderVisibility('hidden')
    } else {
      setHeaderVisibility('visible')
    }
    lastPos = currentPos
  }
}

// Initialize scroll event listener with throttling
throttleAndDebounce(window, 'scroll', onScroll, 100, true)

/**
 * Handle resize events to recalculate header height
 */
const onResize = () => {
  setHeaderHeight() // Recalculate header height on resize
}

// Initialize resize event listener with throttling
throttleAndDebounce(window, 'resize', onResize, 200, true)

/**
 * Initialize the header's state based on the initial scroll position
 */
export const mainHeader = () => {
  const initialPos = window.scrollY

  // Set initial logo width based on scroll position
  const initialLogoWidth = getInitialLogoWidth()
  setLogoWidth(initialLogoWidth)

  // Set initial header position
  if (initialPos > SCROLL_OFFSET) {
    setHeaderPosition('scrolled')
  } else {
    setHeaderPosition('default')
  }

  // Set initial header visibility
  if (initialPos > downThreshold) {
    setHeaderVisibility('hidden')
  } else {
    setHeaderVisibility('visible')
  }

  lastPos = initialPos

  // Set the initial header height
  setHeaderHeight()
}
